import { getObjectKeys } from '@hg-akademie-ban/vamos-viva-type-utils';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  type Theme,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';
import { z } from 'zod';

import {
  type GetAvailableLocationsOutput,
  type GetAvailableRegionenOutput,
  type GetAvailableStandorteByRegionOutput,
  locationArt,
  locationArtSchema,
  type Sitzordnung,
} from '../../../../dtos';
import { content } from '../../../../utils';
import { locationArtLabels } from '../../../../utils/locationArtLabels';
import { FilterDrawer } from '../../../filter/FilterDrawer';
import { sitzordnungLabelByValue } from '../../content';
import { NumberInput } from '../../NumberInput';
import { FilterDrawerAccordion } from './FilterDrawerAccordion';
import {
  type FilterParameters,
  getSelectedFilterCountText,
  hatGruppenraumValues,
} from './ResourceCalendarFilters.utils';

type ResourceCalendarFiltersDrawerProps = {
  readonly open: boolean;
  readonly onClose?: () => void;
  readonly updateFilterValue: <K extends keyof FilterParameters>(key: K, value: FilterParameters[K]) => void;
  readonly deleteAllFilters: () => void;
  readonly filterParameters: FilterParameters;
  readonly availableRegions: GetAvailableRegionenOutput;
  readonly availableStandorte: GetAvailableStandorteByRegionOutput;
  readonly availableLocations: GetAvailableLocationsOutput;
  readonly hiddenFilters?: (keyof FilterParameters)[];
  readonly lockedFilters?: Partial<Record<'regionSapIds' | 'standortSapIds' | 'locationSapIds', boolean>>;
};

const drawerZIndex = (theme: Theme): number => theme.zIndex.modal + 1;
const selectPopperZIndex = (theme: Theme): number => drawerZIndex(theme) + 1;

const CalendarFilterSelectedLabel = (more: number): ReactNode => (
  <Typography variant="paragraph">{`${more} ausgewählt`}</Typography>
);

type PhysischeSitzordnung = Exclude<Sitzordnung, 'ONLINE'>;

const sitzordnungOptions = getObjectKeys(sitzordnungLabelByValue).filter(
  (key): key is PhysischeSitzordnung => key !== 'ONLINE',
);

export const ResourceCalendarFiltersDrawer = ({
  open,
  onClose,
  updateFilterValue,
  deleteAllFilters,
  filterParameters,
  availableLocations,
  availableStandorte,
  availableRegions,
  hiddenFilters,
  lockedFilters,
}: ResourceCalendarFiltersDrawerProps) => {
  const updateLocationArtFilter = (target: HTMLInputElement): void => {
    const value = locationArtSchema.parse(target.value);
    const isChecked = target.checked;

    let newLocationArten: (keyof typeof locationArt)[];
    if (isChecked) {
      newLocationArten = [...filterParameters.locationArten, value];
    } else {
      newLocationArten = filterParameters.locationArten.filter((art) => art !== value);
    }

    updateFilterValue('locationArten', newLocationArten);
  };

  const selectedRegions = availableRegions.filter((r) => filterParameters.regionSapIds.includes(r.regionSapId));
  const selectedStandorte = availableStandorte.filter((s) => filterParameters.standortSapIds.includes(s.standortSapId));
  const selectedLocations = availableLocations.filter((l) => filterParameters.locationSapIds.includes(l.locationSapId));

  return (
    <FilterDrawer open={open} onClose={onClose}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 204px)' }}>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <Stack spacing={1} display="flex" divider={<Divider flexItem />} sx={{ overflowY: 'auto' }}>
            {!hiddenFilters?.includes('regionSapIds') && (
              <FilterDrawerAccordion
                title={`Region ${getSelectedFilterCountText(filterParameters, 'regionSapIds')}`}
                expanded
              >
                <FormControl fullWidth>
                  <Autocomplete
                    id="region-input"
                    data-testid="region-input"
                    disabled={lockedFilters?.regionSapIds === true}
                    size="small"
                    multiple
                    value={selectedRegions}
                    onChange={(_, newValues) => {
                      updateFilterValue(
                        'regionSapIds',
                        newValues.map((region) => region.regionSapId),
                      );
                    }}
                    options={availableRegions}
                    limitTags={0}
                    getLimitTagsText={CalendarFilterSelectedLabel}
                    getOptionLabel={(opt) => opt.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Auswählen"
                        sx={{
                          '& input': {
                            display: () => (selectedRegions.length > 0 ? 'none' : undefined),
                          },
                        }}
                      />
                    )}
                    slotProps={{ popper: { sx: { zIndex: selectPopperZIndex } } }}
                  />
                </FormControl>
              </FilterDrawerAccordion>
            )}

            {!hiddenFilters?.includes('standortSapIds') && (
              <FilterDrawerAccordion
                title={`Standort ${getSelectedFilterCountText(filterParameters, 'standortSapIds')}`}
              >
                <FormControl fullWidth>
                  <Autocomplete
                    id="standort-input"
                    disabled={lockedFilters?.standortSapIds === true}
                    size="small"
                    multiple
                    value={selectedStandorte}
                    onChange={(_, newValues) => {
                      updateFilterValue(
                        'standortSapIds',
                        newValues.map((ort) => ort.standortSapId),
                      );
                    }}
                    options={availableStandorte}
                    limitTags={0}
                    getLimitTagsText={CalendarFilterSelectedLabel}
                    getOptionLabel={(opt) => opt.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Auswählen"
                        sx={{
                          '& input': {
                            display: () => (selectedStandorte.length > 0 ? 'none' : undefined),
                          },
                        }}
                      />
                    )}
                    slotProps={{ popper: { sx: { zIndex: selectPopperZIndex } } }}
                  />
                </FormControl>
              </FilterDrawerAccordion>
            )}

            {!hiddenFilters?.includes('locationSapIds') && (
              <FilterDrawerAccordion
                title={`Location ${getSelectedFilterCountText(filterParameters, 'locationSapIds')}`}
              >
                <FormControl fullWidth>
                  <Autocomplete
                    id="location-input"
                    disabled={lockedFilters?.locationSapIds === true}
                    size="small"
                    multiple
                    value={selectedLocations}
                    onChange={(_, newValues) => {
                      updateFilterValue(
                        'locationSapIds',
                        newValues.map((location) => location.locationSapId),
                      );
                    }}
                    options={availableLocations}
                    limitTags={0}
                    getLimitTagsText={CalendarFilterSelectedLabel}
                    getOptionLabel={(opt) => opt.kuerzel}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Auswählen"
                        sx={{
                          '& input': {
                            display: () => (selectedLocations.length > 0 ? 'none' : undefined),
                          },
                        }}
                      />
                    )}
                    slotProps={{ popper: { sx: { zIndex: selectPopperZIndex } } }}
                  />
                </FormControl>
              </FilterDrawerAccordion>
            )}

            {!hiddenFilters?.includes('raumgroesse') && (
              <FilterDrawerAccordion title="Raumgröße">
                <NumberInput
                  id="raumgroesse-input"
                  tooltip={`Mindestraumgröße in ${content.units.qm}`}
                  min={1}
                  value={filterParameters.raumgroesse}
                  onChange={(number): void => updateFilterValue('raumgroesse', number)}
                />
              </FilterDrawerAccordion>
            )}

            {!hiddenFilters?.includes('teilnehmerErwartet') && !hiddenFilters?.includes('sitzordnung') && (
              <FilterDrawerAccordion title="Raumkapazität" expanded>
                <Box display="grid" gap={1} gridTemplateColumns="repeat(2, minmax(0, 1fr))">
                  <Autocomplete
                    size="small"
                    fullWidth
                    value={filterParameters.sitzordnung}
                    onChange={(_, newValue) => updateFilterValue('sitzordnung', newValue)}
                    options={sitzordnungOptions}
                    getOptionLabel={(opt) => sitzordnungLabelByValue[opt]}
                    renderInput={(params) => <TextField {...params} label="Bestuhlung" />}
                    slotProps={{ popper: { sx: { zIndex: selectPopperZIndex } } }}
                  />
                  <NumberInput
                    id="teilnehmer-erwartet-input"
                    label="Erwartete Teilnehmer"
                    min={1}
                    value={filterParameters.teilnehmerErwartet}
                    onChange={(number) => updateFilterValue('teilnehmerErwartet', number)}
                  />
                  <Typography
                    variant="labelSmLight"
                    color="text.secondary"
                    sx={{ gridColumn: '1 / span 2', lineHeight: (theme) => theme.typography.body1.lineHeight }}
                  >
                    Zum Filtern auf Raumkapazität müssen sowohl “Bestuhlung” als auch “Erwartete Teilnehmer” ausgefüllt
                    werden.
                  </Typography>
                </Box>
              </FilterDrawerAccordion>
            )}

            {!hiddenFilters?.includes('locationArten') && (
              <FilterDrawerAccordion
                title={`Art der Location ${getSelectedFilterCountText(filterParameters, 'locationArten')}`}
                expanded
              >
                <FormGroup
                  onChange={(evt) => updateLocationArtFilter(z.instanceof(HTMLInputElement).parse(evt.target))}
                >
                  {getObjectKeys(locationArt).map((key) => (
                    <FormControlLabel
                      key={key + 'checkbox'}
                      value={key}
                      control={<Checkbox />}
                      label={locationArtLabels[key]}
                      checked={filterParameters.locationArten.includes(key)}
                    />
                  ))}
                </FormGroup>
              </FilterDrawerAccordion>
            )}

            {!hiddenFilters?.includes('locationHatGruppenraum') && (
              <FilterDrawerAccordion title="Ausstattung der Location" expanded>
                <FormControl fullWidth>
                  <Autocomplete
                    id="hatGruppenraum-input"
                    size="small"
                    value={hatGruppenraumValues.find((item) => item.value === filterParameters.locationHatGruppenraum)}
                    onChange={(_, newValue) => {
                      updateFilterValue('locationHatGruppenraum', newValue?.value ?? null);
                    }}
                    options={hatGruppenraumValues}
                    getOptionLabel={(opt) => opt.label}
                    renderInput={(params) => <TextField {...params} label="Gruppenraum an der Location" />}
                    slotProps={{ popper: { sx: { zIndex: selectPopperZIndex } } }}
                  />
                </FormControl>
              </FilterDrawerAccordion>
            )}

            {!hiddenFilters?.includes('freieTage') && (
              <FilterDrawerAccordion title="Min. freie Tage" expanded>
                <NumberInput
                  id="freietage-input"
                  min={1}
                  max={5}
                  tooltip="Mindestanzahl an zusammenhängend buchbaren Tagen eines Raumes (minimal 1, maximal 5). Filtert nur Seminarräume."
                  value={filterParameters.freieTage}
                  onChange={(number): void => updateFilterValue('freieTage', number)}
                />
              </FilterDrawerAccordion>
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexShrink: 0 }}>
          <Button
            onClick={deleteAllFilters}
            variant="outlined"
            size="small"
            sx={{ flex: 1, backgroundColor: 'white', pt: 1 }}
          >
            Alle zurücksetzen
          </Button>

          <Button onClick={onClose} variant="contained" size="small" sx={{ flex: 1 }}>
            Anwenden
          </Button>
        </Stack>
      </Box>
    </FilterDrawer>
  );
};
